import { Button, Menu, MenuList, Stack } from '@dtx-company/design-system/src'
import { FC, useRef, useState } from 'react'
import { FLOWCODE_MARKETING_BASE_URL, Routes } from '@dtx-company/true-common/src/constants/routes'
import { HeaderLogo } from '../../layout/header/v2/HeaderLogo'
import { Login } from '@mui/icons-material'
import { MenuItem } from '@mui/material'
import { constructEmailLink } from '@dtx-company/flowcode-utils/src'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import Link from 'next/link'
import events from '@dtx-company/inter-app/src/event-tracking/events/nav'

interface BuyPlanPageHeaderNavProps {
  isLoggedIn: boolean
  isPositionFixed: boolean
  planName: string
}

export const BuyPlanPageHeaderNav: FC<BuyPlanPageHeaderNavProps> = ({
  isLoggedIn,
  isPositionFixed,
  planName
}) => {
  const [openContactSalesMenu, setOpenContactSalesMenu] = useState(false)
  const contactSalesRef = useRef<HTMLButtonElement | null>(null)
  const { isMobile } = useScreenSizeBreakpoints()

  return (
    <Stack
      direction={'row'}
      sx={{
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'background.paper',
        position: isPositionFixed ? 'fixed' : 'relative',
        top: isPositionFixed ? 0 : undefined,
        maxWidth: '100vw',
        gap: 4,
        px: {
          xs: 3,
          md: 4
        },
        py: 2
      }}
    >
      <HeaderLogo href={FLOWCODE_MARKETING_BASE_URL} />
      <Stack
        direction={'row'}
        sx={{
          gap: { xs: 2, md: 4 }
        }}
      >
        <Button
          ref={contactSalesRef}
          color={'secondary'}
          size={isMobile ? 'small' : 'medium'}
          onClick={() => {
            fireAnalyticsEvent('BuyPlanPage_Contact_Sales_Clicked')
            setOpenContactSalesMenu(true)
          }}
        >
          Contact Sales
        </Button>
        {isLoggedIn && (
          <Button
            size={isMobile ? 'small' : 'medium'}
            component={Link}
            href={Routes.ANALYTICS_CODES}
            color="tertiary"
            startIcon={Login}
            onClick={() => events.Global_MarketingNav_Clicked_DashboardButton()}
          >
            Dashboard
          </Button>
        )}
      </Stack>
      <Menu
        open={openContactSalesMenu}
        onClose={() => setOpenContactSalesMenu(false)}
        anchorEl={contactSalesRef.current}
        transformOrigin={{
          vertical: -10,
          horizontal: 'left'
        }}
      >
        <MenuList
          autoFocusItem
          data-testid="log-in-menu"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <MenuItem
            component={Link}
            href={constructEmailLink({
              subject: 'Contact Sales',
              body:
                'Hi,\n \n' +
                `I am interested in Flowcode. Could you share more information on ${planName} pricing? 
                \n \n Thank you!`,
              email: 'partner@flowcode.com'
            })}
            target="_blank"
            data-testid={'email-option'}
            onClick={() => {
              fireAnalyticsEvent('Global_MarketingNav_Clicked_Email')
              setOpenContactSalesMenu(false)
            }}
          >
            Email
          </MenuItem>
          <MenuItem
            data-testid={'chat-option'}
            onClick={() => {
              fireAnalyticsEvent('Global_MarketingNav_Clicked_Chat')
              window?.HubSpotConversations?.widget.open()
              setOpenContactSalesMenu(false)
            }}
          >
            Chat
          </MenuItem>
          <MenuItem
            component={Link}
            href={'tel:9176133357'}
            data-testid={'call-us-option'}
            target="_blank"
            onClick={() => {
              fireAnalyticsEvent('Global_MarketingNav_Clicked_Call_Us')
              setOpenContactSalesMenu(false)
            }}
          >
            +1 (917) 613-3357
          </MenuItem>
        </MenuList>
      </Menu>
    </Stack>
  )
}
